import type { SocialButtonProps, SocialIdPHandler } from 'app/components/authentication/LoginButtonState';
import { SocialAuthButton } from './SocialAuthButton';
import { env } from 'app/lib/env';
import { log } from 'app/lib/monitoring';
import type * as AuthSession from 'expo-auth-session';
import * as Google from 'expo-auth-session/providers/google';
import * as WebBrowser from 'expo-web-browser';
import { jwtDecode } from 'jwt-decode';
WebBrowser.maybeCompleteAuthSession();

function handleGoogleRequest(
  authResult: AuthSession.AuthSessionResult | null,
  handleSocialIdP: SocialIdPHandler,
  setButtonState: (value: boolean) => void,
) {
  try {
    if (authResult?.type === 'success' && authResult.params.id_token) {
      const idToken = authResult.params.id_token;
      log('Google auth success', { hasIdToken: !!idToken });
      const email = jwtDecode<{ email?: string }>(idToken).email;
      if (!email) {
        log('Error: No email found in Google ID token');
        setButtonState(false);
        return;
      }
      handleSocialIdP({
        email,
        token: idToken,
        provider: 'Google',
        onError: () => setButtonState(false),
      });
    } else {
      log('Google auth failed or cancelled', { type: authResult?.type });
      setButtonState(false);
    }
  } catch (error) {
    log('Google signup error:', error);
    setButtonState(false);
  }
}

export function GoogleLoginButton({ handleSocialIdP, loginButtonsState, iconOnly }: SocialButtonProps) {
  const { setLoginButton } = loginButtonsState;

  const [, , promptAsync] = Google.useIdTokenAuthRequest({
    iosClientId: env.GOOGLE_CLIENT_ID,
    androidClientId: env.GOOGLE_CLIENT_ID,
    webClientId: env.GOOGLE_CLIENT_ID,
    redirectUri: env.OAUTH_REDIRECT_URI,
    scopes: ['email', 'profile', 'openid'],
  });

  const toggleButtonStateToBe = (toState: boolean) => {
    setLoginButton('Google', toState);
  };

  const onGoogleRequestHandler = (authResult: AuthSession.AuthSessionResult | null) =>
    handleGoogleRequest(authResult, handleSocialIdP, toggleButtonStateToBe);

  return (
    <SocialAuthButton
      iconOnly={iconOnly}
      loginButtonsState={loginButtonsState}
      onPress={() => {
        toggleButtonStateToBe(true);
        void promptAsync()
          .then(onGoogleRequestHandler)
          .catch((error: unknown) => {
            log('Google login prompt error:', error);
            toggleButtonStateToBe(false);
          });
      }}
      provider="Google"
    />
  );
}
